import { call, put, fork, takeLatest, select, take } from 'redux-saga/effects';
import {
  receiveDailyConditions,
  receiveHourlyConditions,
  receiveCurrentConditions,
  failureWeatherConditions,
} from '../actions/weatherForecastActions';
import {
  REQUEST_CURRENT_CONDITIONS,
  REQUEST_HOURLY_CONDITION,
  REQUEST_DAILY_CONDITIONS,
  REQUEST_CURRENT_CONDITIONS_BY_LOCATION,
  REQUEST_HOURLY_CONDITION_BY_LOCATION,
  REQUEST_DAILY_CONDITIONS_BY_LOCATION,
} from '../constants/actionTypes';
import { requestCurrentConditionsByLocation, requestDailyForecastByLocation, requestHourlyForecastByLocation } from '../requesters/weatherForecastByLocationRequesters';
import {
  requestCurrentConditions,
  requestHourlyForecast,
  requestDailyForecast,
} from '../requesters/weatherForecastRequesters';
import { FeatureFlagKey } from '../../types';
import { checkFeatureFlag } from './utils';

function* waitForFeatureFlags() {
  const loaded = yield select(state => state.feature.featureFlagsLoaded);
  if (!loaded) {
    yield take('RECEIVE_FEATURES');
  }
}

export function* getCurrentConditions(action) {
  const { field: { id } } = action;
  try {
    const currentConditions = yield call(
      requestCurrentConditions,
      id
    );
    const weatherData = currentConditions.results[0];
    yield put(receiveCurrentConditions(weatherData.value.variables));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getHourlyConditions(action) {
  try {
    const { field: { id } } = action;
    const hourlyConditions = yield call(requestHourlyForecast, id);
    const weatherData = hourlyConditions.results[0];
    yield put(receiveHourlyConditions(weatherData.value));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getDailyConditions(action) {
  try {
    const { field: { id } } = action;
    // Check if dmb.spray-assist-feature is On
    yield* waitForFeatureFlags();
    const isSprayAssistEnabled = yield* checkFeatureFlag(FeatureFlagKey.DMB_SPRAY_ASSIST);
    // if dmb.spray-assist-feature is On, call requestDailyForecast with True parameter to get spray assist information
    const dailyConditions = yield call(requestDailyForecast, id, isSprayAssistEnabled);
    const weatherData = dailyConditions.results[0];
    yield put(receiveDailyConditions(weatherData.value));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getCurrentConditionsWatcher() {
  yield takeLatest(REQUEST_CURRENT_CONDITIONS, getCurrentConditions);
}

export function* getHourlyConditionsWatcher() {
  yield takeLatest(REQUEST_HOURLY_CONDITION, getHourlyConditions);
}

export function* getDailyConditionsWatcher() {
  yield takeLatest(REQUEST_DAILY_CONDITIONS, getDailyConditions);
}

export function* getCurrentConditionsByLocation() {
  try {
    const { currentUser, field } = yield select();
    const { centroid: { coordinates: [long, lat] } } = field;

    const currentConditions = yield call(
      requestCurrentConditionsByLocation,
      lat,
      long,
      currentUser.id
    );
    const weatherData = currentConditions.results[0];
    yield put(receiveCurrentConditions(weatherData.value.variables));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getHourlyConditionsByLocation() {
  try {
    const { currentUser, field } = yield select();
    const { centroid: { coordinates: [long, lat] } } = field;

    const hourlyConditions = yield call(
      requestHourlyForecastByLocation,
      lat,
      long,
      currentUser.id
    );
    const weatherData = hourlyConditions.results[0];
    yield put(receiveHourlyConditions(weatherData.value));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getDailyConditionsByLocation() {
  try {
    const { currentUser, field } = yield select();
    const { centroid: { coordinates: [long, lat] } } = field;

    const dailyConditions = yield call(
      requestDailyForecastByLocation,
      lat,
      long,
      currentUser.id
    );
    const weatherData = dailyConditions.results[0];
    yield put(receiveDailyConditions(weatherData.value));
  } catch (error) {
    yield put(failureWeatherConditions(error));
  }
}

export function* getCurrentConditionsByLocationWatcher() {
  yield takeLatest(REQUEST_CURRENT_CONDITIONS_BY_LOCATION, getCurrentConditionsByLocation);
}

export function* getHourlyConditionsByLocationWatcher() {
  yield takeLatest(REQUEST_HOURLY_CONDITION_BY_LOCATION, getHourlyConditionsByLocation);
}

export function* getDailyConditionsByLocationWatcher() {
  yield takeLatest(REQUEST_DAILY_CONDITIONS_BY_LOCATION, getDailyConditionsByLocation);
}

export default function* rootSaga() {
  yield fork(getCurrentConditionsWatcher);
  yield fork(getHourlyConditionsWatcher);
  yield fork(getDailyConditionsWatcher);
  yield fork(getCurrentConditionsByLocationWatcher);
  yield fork(getHourlyConditionsByLocationWatcher);
  yield fork(getDailyConditionsByLocationWatcher);
}
